import React from "react";
import { Box, Container, Grid, Typography, makeStyles } from "@material-ui/core";
import QRCode from "react-qr-code";
import {
  USMQRCodeSize,
  HydrovacScreeningQuestions as screeningQuestions,
  HydrovacInspectionQuestions as inspectionQuestions,
} from "../../utils/constants";

import { HydrovacTicket } from "../../interface/api/HydrovacTicket";
import { PrintableTicketProps } from "../../interface/PrintableTicketProps";
import { hydrovacTicketNumber } from "../../utils/parser";
import { formatDate, formatDateTime, formatTime } from "../../utils/date-helper";

const useStyles = makeStyles({
  sectionHeader: {
    backgroundColor: "#4F4F4F",
    color: "#fff",
    textAlign: "center",
    padding: "8px",
    marginBottom: 16,
  },
  questionRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 0",
    borderBottom: "1px solid #eee",
  },
  fieldBox: {
    border: "1px solid #333",
    padding: "8px",
    textAlign: "center",
    minHeight: 60,
    height: "100%",
    boxSizing: "border-box" as const,
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    overflowWrap: "anywhere" as const,
  },
  fieldBoxLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    marginBottom: 4,
  },
  fieldBoxValue: {
    fontSize: "0.85rem",
    minHeight: 20,
  },
  fieldBoxSub: {
    fontSize: "0.65rem",
    color: "#666",
    marginTop: 4,
  },
  signatureImage: {
    maxWidth: "100%",
    maxHeight: 150,
    display: "block",
  },
});


/**
 * The printed hydrovac ticket, read-only. Rendered both by the staff-only
 * /hydrovac-digital-ticket/:id route and, once a ticket is received, to drivers
 * who scan its QR code.
 */
const HydrovacPrintableTicket = ({ ticket, ticketUrl }: PrintableTicketProps<HydrovacTicket>) => {
  const classes = useStyles();

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          border: "2px solid black",
          padding: "20px",
          marginTop: "20px",
          marginBottom: "20px",
        }}
        style={{ backgroundColor: "#fff" }}
      >
        {/* Header Row: Status + USM TICKET # + QR Code */}
        <Grid container spacing={2} alignItems="center" style={{ marginBottom: 20 }}>
          <Grid item xs={5}>
            {ticket.isComplete && (
              <Box
                style={{ backgroundColor: "green" }}
                sx={{
                  border: "3px solid green",
                  borderRadius: 6,
                  textAlign: "center",
                  padding: 5,
                }}
              >
                <Typography
                  style={{
                    fontSize: 20,
                    textTransform: "uppercase",
                    letterSpacing: 3,
                    color: "white",
                    whiteSpace: "nowrap",
                  }}
                >
                  Complete
                </Typography>
              </Box>
            )}
            {ticket.isReceived && !ticket.isComplete && (
              <Box
                style={{ backgroundColor: "#ff9800" }}
                sx={{
                  border: "3px solid #ff9800",
                  borderRadius: 6,
                  textAlign: "center",
                  padding: 5,
                }}
              >
                <Typography
                  style={{
                    fontSize: 20,
                    textTransform: "uppercase",
                    letterSpacing: 3,
                    color: "white",
                  }}
                >
                  {ticket.isPendingHeadspace ? "Pending Headspace" : "Received"}
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={7} style={{ textAlign: "right" }}>
            <Box style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 16 }}>
              <Box>
                <Typography variant="body2" style={{ display: "inline" }}>
                  USM TICKET #{" "}
                </Typography>
                <Typography
                  variant="h5"
                  style={{ color: "red", display: "inline", fontWeight: 600 }}
                >
                  {hydrovacTicketNumber(ticket)}
                </Typography>
              </Box>
              {ticketUrl && (
                <QRCode value={ticketUrl} size={USMQRCodeSize} />
              )}
            </Box>
          </Grid>
        </Grid>
        {/* Section 1: Source Operator */}
        <Box className={classes.sectionHeader}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, color: "#fff" }}>
            SOURCE OPERATOR
          </Typography>
          <Typography variant="body2" style={{ color: "#fff" }}>
            Small Quantity Source Site Screening Assessment
          </Typography>
        </Box>

        <Grid container spacing={3} style={{ marginBottom: 20 }}>
          {/* Left column: address and details */}
          <Grid item xs={12} sm={6}>
            {ticket.contractorCompanyName && (
              <Typography style={{ marginBottom: 8 }}>
                <b>Contractor:</b> {ticket.contractorCompanyName}
              </Typography>
            )}
            {ticket.propertyAddress && (
              <Typography style={{ marginBottom: 8 }}>
                <b>Property Address:</b> {ticket.propertyAddress}
              </Typography>
            )}
            {ticket.landUse && (
              <Typography style={{ marginBottom: 8 }}>
                <b>Land Use:</b> {ticket.landUse}
              </Typography>
            )}
            {ticket.descriptionOfActivity && (
              <Typography style={{ marginBottom: 8 }}>
                <b>Description of Activity:</b> {ticket.descriptionOfActivity}
              </Typography>
            )}
            {ticket.workOrderNumber && (
              <Typography style={{ marginBottom: 8 }}>
                <b>Work Order #:</b> {ticket.workOrderNumber}
              </Typography>
            )}
            {ticket.propertyOwner && (
              <Typography style={{ marginBottom: 8 }}>
                <b>Property Owner:</b> {ticket.propertyOwner}
              </Typography>
            )}
            {ticket.shippingCompany && (
              <Typography style={{ marginBottom: 8 }}>
                <b>Shipping Company:</b> {ticket.shippingCompany}
              </Typography>
            )}
            {ticket.shippingDriver && (
              <Typography style={{ marginBottom: 8 }}>
                <b>Shipping Driver:</b> {ticket.shippingDriver}
              </Typography>
            )}
            {ticket.truckNumber && (
              <Typography style={{ marginBottom: 8 }}>
                <b>Truck #:</b> {ticket.truckNumber}
              </Typography>
            )}
            {ticket.truckLicensePlate && (
              <Typography style={{ marginBottom: 8 }}>
                <b>Licence Plate:</b> {ticket.truckLicensePlate}
              </Typography>
            )}
            {ticket.timeLoaded && (
              <>
                <Typography style={{ marginBottom: 8 }}>
                  <b>Date of Transport:</b> {formatDate(ticket.timeLoaded, ticket.isSoilFLOLoad)}
                </Typography>
                <Typography style={{ marginBottom: 8 }}>
                  <b>Time Left Source Site:</b> {formatTime(ticket.timeLoaded, ticket.isSoilFLOLoad)}
                </Typography>
              </>
            )}
            <Typography style={{ marginBottom: 8 }}>
              <b>Time Arrived at USM:</b>{" "}
              {ticket.timeUnloaded ? formatDateTime(ticket.timeUnloaded, false) : "—"}
            </Typography>
          </Grid>

          {/* Right column: driver signature */}
          <Grid item xs={12} sm={6}>
            <Box style={{ marginBottom: 16 }}>
              <Typography style={{ marginBottom: 4 }}>
                <b>Driver Signature:</b>
              </Typography>
              {ticket.driverSignature ? (
                <img
                  src={ticket.driverSignature}
                  alt="Driver Signature"
                  className={classes.signatureImage}
                />
              ) : (
                <Typography color="textSecondary">—</Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Screening Questions */}
        <Typography
          variant="subtitle2"
          style={{ fontWeight: 600, marginBottom: 8 }}
        >
          Small Source Site Screening Questions
        </Typography>
        <Box sx={{ marginBottom: 20 }}>
          {screeningQuestions.map(({ field, label }) => {
            const value = (ticket as any)[field];
            if (!value) return null;
            return (
              <div key={field} className={classes.questionRow}>
                <Typography variant="body2" style={{ flex: 1 }}>{label}</Typography>
                <Typography variant="body2" style={{ fontWeight: 600, marginLeft: 8 }}>
                  {value}
                </Typography>
              </div>
            );
          })}
        </Box>

        <div style={{ border: "1px solid black", marginBottom: 16 }} />

        {/* Section 2: Field Screening And Assessment Form */}
        <Box className={classes.sectionHeader}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, color: "#fff" }}>
            UNITED SOILS MANAGEMENT
          </Typography>
          <Typography variant="body2" style={{ color: "#fff" }}>
            Field Screening And Assessment Form
          </Typography>
        </Box>

        {/* Field Assessment Boxes */}
        {(() => {
          const inspectorName = ticket.padApprovedByName || "—";
          return (
            <Grid container spacing={1} style={{ marginBottom: 16 }}>
              <Grid item xs={4} sm={2}>
                <Box className={classes.fieldBox}>
                  <Typography className={classes.fieldBoxLabel}>Headspace Reading</Typography>
                  <Typography className={classes.fieldBoxValue}>
                    {ticket.headspaceReading ?? "—"}
                  </Typography>
                  <Typography className={classes.fieldBoxSub}>(Maximum 200 ppm)</Typography>
                </Box>
              </Grid>
              <Grid item xs={4} sm={3}>
                <Box className={classes.fieldBox}>
                  <Typography className={classes.fieldBoxLabel}>Visual (sheen) Inspection</Typography>
                  <Typography className={classes.fieldBoxValue}>
                    {ticket.visualInspectionResult ?? "—"}
                  </Typography>
                  <Typography className={classes.fieldBoxSub}>USM Auth: {inspectorName}</Typography>
                </Box>
              </Grid>
              <Grid item xs={4} sm={2}>
                <Box className={classes.fieldBox}>
                  <Typography className={classes.fieldBoxLabel}>Entry into Site</Typography>
                  <Typography className={classes.fieldBoxValue}>—</Typography>
                  <Typography className={classes.fieldBoxSub}>USM Auth: {inspectorName}</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box className={classes.fieldBox}>
                  <Typography className={classes.fieldBoxLabel}>
                    Containment Pad location & cell reference #
                  </Typography>
                  <Typography className={classes.fieldBoxValue}>
                    {`${ticket.containmentPadName ?? "—"}${
                      ticket.containmentBay ? ` - Bay ${ticket.containmentBay}` : ""
                    }`}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Box className={classes.fieldBox}>
                  <Typography className={classes.fieldBoxLabel}>Clearance</Typography>
                  <Typography className={classes.fieldBoxValue}>—</Typography>
                  <Typography className={classes.fieldBoxSub}>USM Auth: {inspectorName}</Typography>
                </Box>
              </Grid>
            </Grid>
          );
        })()}

        {/* Inspection Checklist */}
        <Typography
          variant="subtitle2"
          style={{ fontWeight: 600, marginBottom: 8 }}
        >
          Field Screening and Inspection Checklist
        </Typography>
        <Box sx={{ marginBottom: 20 }}>
          {inspectionQuestions.map(({ field, label }) => {
            const value = (ticket as any)[field];
            return (
              <div key={field} className={classes.questionRow}>
                <Typography variant="body2" style={{ flex: 1 }}>{label}</Typography>
                <Typography variant="body2" style={{ fontWeight: 600, marginLeft: 8 }}>
                  {value === true ? "Yes" : "No"}
                </Typography>
              </div>
            );
          })}
        </Box>

        {/* Bottom section */}
        {(() => {
          const inspectorName = ticket.padApprovedByName || "—";
          const sampleDate = ticket.padApprovalDate
            ? formatDate(ticket.padApprovalDate, true)
            : "—";
          return (
            <>
              <Grid container spacing={1} style={{ marginBottom: 16 }}>
                <Grid item xs={6}>
                  <Box className={classes.fieldBox}>
                    <Typography className={classes.fieldBoxLabel}>Sample Reference #</Typography>
                    <Typography className={classes.fieldBoxValue}>
                      {ticket.padSampleReferenceNumber
                        ? `${sampleDate} — ${ticket.padSampleReferenceNumber}`
                        : "—"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box className={classes.fieldBox}>
                    <Typography className={classes.fieldBoxLabel}>QP Approval</Typography>
                    {ticket.padInspectorSignature ? (
                      <img src={ticket.padInspectorSignature} alt="QP Signature"
                        style={{ maxWidth: "100%", maxHeight: 40, display: "block", margin: "4px auto" }} />
                    ) : (
                      <Typography className={classes.fieldBoxValue}>—</Typography>
                    )}
                    <Typography className={classes.fieldBoxSub}>USM Auth: {inspectorName}</Typography>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ marginBottom: 10 }}>
                <Typography style={{ marginBottom: 4 }}>
                  <b>USM Inspector's Name:</b> {inspectorName}
                </Typography>
                <Typography style={{ marginBottom: 4 }}><b>QP Signature:</b></Typography>
                {ticket.padInspectorSignature ? (
                  <img src={ticket.padInspectorSignature} alt="QP Signature"
                    style={{ maxWidth: "100%", maxHeight: 100, display: "block" }} />
                ) : (
                  <Typography variant="body2" color="textSecondary">—</Typography>
                )}
              </Box>
            </>
          );
        })()}
      </Box>
      <Box pb={4} />
    </Container>
  );
};

export default HydrovacPrintableTicket;
